/* File: main.jsx
 * Path: ananunadm/src/components/main/main.jsx
 */
// main.jsx - Optimized version

import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import * as API from "@/helpers/api";
import config from "@/config";
import { useUser } from "@/helpers/userContext";
import {
	AppShell,
	Image,
	Flex,
	Group,
	ActionIcon,
	useMantineTheme,
	Text,
	Header,
	Navbar,
	Drawer,
	Box,
	ScrollArea
} from "@mantine/core";
import { IconLayoutSidebar } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { useResponsive } from "@/helpers/is-mobile";
import Sidebar from "./sidebar";
import Notifications from "./notifications";
import Loading from "@/helpers/loading";
import { useLocation } from "react-router-dom";
import { useSocket } from "@/helpers/socket";
import { T } from "@/helpers/translator";
import {
	describeRoute,
	finalizeEvent,
	getClientSessionId,
	newEventId,
	recordEvent
} from "@/helpers/platformEvents";
import ActivityTracker from "./activityTracker.jsx";
import { useLang } from "@/helpers/language";
import UserInfoModal from "./userInfoModal";
import HeaderMenuButtons from "./headerMenuButtons";
import PhotosPickerController from "./PhotosPickerController";
import { BreadcrumbsComponent } from "./breadcrumbs";
import { useGlobal } from "@/helpers/global";
import { ThemeToggle } from "./theme-toggle/themeToggle";
import { TrashBadge } from "../trash/TrashBadge";
import GlobalSearch from "./globalSearch.jsx";
import Language from "./language.jsx";
import YerevanWeather from "./YerevanWeather";
import { useCalendarReminderPoller } from "@/helpers/useCalendarReminderPoller";
import {
	filterMenuItemsByReadPermission,
	filterPlatformsByUserAccess
} from "@/helpers/platformMenuAccess";
import { SecurityAlertsBadgeProvider } from "@/helpers/securityAlertsBadgeContext";
import { TICKET_MENU_RELOAD_EVENT } from "@/helpers/ticketMenuReload";
import {
	fetchTicketDomainAndTypeDocs,
	insertTicketDomainMenuItems
} from "@/helpers/ticketMenuDynamic";
import AssistantDrawer from "../ai/safe-assistant/AssistantDrawer";

const SIDEBAR_WIDTH_PX = 300;
const SIDEBAR_TOGGLE_MS = 280;
const SIDEBAR_TOGGLE_EASING = "cubic-bezier(0.4, 0, 0.2, 1)";

/** Convert a #rgb/#rrggbb module color into an rgba() string with the given alpha. */
function hexToRgba(hex, alpha = 1) {
	if (!hex || typeof hex !== "string") return null;
	let h = hex.trim().replace(/^#/, "");
	if (h.length === 3) {
		h = h
			.split("")
			.map((c) => c + c)
			.join("");
	}
	if (h.length !== 6) return null;
	const num = Number.parseInt(h, 16);
	if (Number.isNaN(num)) return null;
	const r = (num >> 16) & 255;
	const g = (num >> 8) & 255;
	const b = num & 255;
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const isUserMentionedInMessage = ({ message = {}, userId = "" } = {}) => {
	const normalizedUserId = String(userId || "");
	if (!normalizedUserId) return false;
	return (Array.isArray(message?.mentions) ? message.mentions : []).some(
		(mentionId) => String(mentionId?._id || mentionId) === normalizedUserId
	);
};

export default function Main({ component: Component }) {
	const [isBusy, setIsBusy] = useState(true);
	const { isMobile, isTablet } = useResponsive();

	const [opened, { toggle, close: closeSidebar }] = useDisclosure(!isMobile);
	const isCompactNav = isMobile || isTablet;
	const closeSidebarAfterNav = useCallback(() => {
		if (isCompactNav) closeSidebar();
	}, [isCompactNav, closeSidebar]);
	const location = useLocation();
	const { user } = useUser();
	const { socket, setNewNotification } = useSocket();
	const theme = useMantineTheme();
	const { lang } = useLang();
	const [platformMenuOpened, OpenClose] = useDisclosure(false);
	const [assistantOpened, assistantHandlers] = useDisclosure(false);
	const { platforms, setPlatforms, setFilteredMenu, setCurrentPlatformIndex } = useGlobal();

	// Poll calendar events for due reminder alarms and surface them as notifications
	useCalendarReminderPoller();

	// Optimized function to find current platform from URL
	const findCurrentPlatform = useCallback((platformsList, pathname) => {
		if (!platformsList?.length) return { platform: null, index: -1 };

		const index = platformsList.findIndex(
			(platform) => pathname.startsWith(`/${platform.route}/`) || pathname === `/${platform.route}`
		);

		return {
			platform: index >= 0 ? platformsList[index] : null,
			index
		};
	}, []);

	// Memoized filtered menu calculation
	const getFilteredMenu = useCallback(() => {
		if (!platforms?.length) {
			return [];
		}

		const { platform: currentPlatform, index } = findCurrentPlatform(platforms, location.pathname);

		// Update current platform index in global state
		if (index >= 0) {
			setCurrentPlatformIndex(index);
		}

		return currentPlatform?.menu ? filterMenuItemsByReadPermission(currentPlatform.menu, user) : [];
	}, [platforms, location.pathname, user, findCurrentPlatform, setCurrentPlatformIndex]);

	// Initialize platforms: env-enabled menu + ticket domains/types from API + RBAC
	useEffect(() => {
		let cancelled = false;
		(async () => {
			const filteredMenuItems = config.menu;

			if (filteredMenuItems.length === 0) {
				console.error("No platforms are enabled in the environment settings.");
				setPlatforms([]);
				return;
			}

			const { domainDocs, ticketTypeDocs } = await fetchTicketDomainAndTypeDocs();
			const withDomains = insertTicketDomainMenuItems(
				filteredMenuItems,
				domainDocs,
				ticketTypeDocs
			);
			if (!cancelled) setPlatforms(filterPlatformsByUserAccess(withDomains, user));
		})();
		return () => {
			cancelled = true;
		};
	}, [user, setPlatforms]);

	// Rebuild ticket sidebar after domain/ticket-type CRUD (see ticketMenuReload.js + CRUD.js)
	useEffect(() => {
		const onReload = () => {
			const filteredMenuItems = config.menu;
			if (filteredMenuItems.length === 0) return;
			(async () => {
				const { domainDocs, ticketTypeDocs } = await fetchTicketDomainAndTypeDocs();
				const withDomains = insertTicketDomainMenuItems(
					filteredMenuItems,
					domainDocs,
					ticketTypeDocs
				);
				setPlatforms(filterPlatformsByUserAccess(withDomains, user));
			})();
		};
		window.addEventListener(TICKET_MENU_RELOAD_EVENT, onReload);
		return () => window.removeEventListener(TICKET_MENU_RELOAD_EVENT, onReload);
	}, [user, setPlatforms]);

	// Update filtered menu on location change
	useEffect(() => {
		setFilteredMenu(getFilteredMenu());
	}, [location.pathname, platforms, getFilteredMenu, setFilteredMenu]);

	// Auto-close sidebar after route change on mobile / tablet (drawer or narrow navbar).
	// Only pathname matters: query-only navigations (e.g. ?messageId= deep links from search) must not collapse the nav.
	// Do not list `opened` in deps — when the user opens the drawer, `opened` becomes true and this effect would
	// run again and call closeSidebar() immediately (mobile nav could never stay open).
	useEffect(() => {
		if (isCompactNav && opened) closeSidebar();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- close only on pathname / compact-mode changes
	}, [location.pathname, isCompactNav, closeSidebar]);

	// Track page lifecycle: readable route, previous page, session, stayed time,
	// scroll depth, and the next page returned to.
	useEffect(() => {
		if (!user?._id) return undefined;
		const eventId = newEventId();
		const openedAt = Date.now();
		const route = describeRoute(location.pathname);
		const previousPathname = window.sessionStorage.getItem("apit:previous-pathname") || "";
		const previous = previousPathname ? describeRoute(previousPathname) : null;
		const clicked = [];

		const clickListener = (event) => {
			const element = event.target?.closest?.("button, a, [role='button']");
			const label = String(element?.getAttribute?.("aria-label") || element?.textContent || "")
				.trim()
				.replace(/\s+/g, " ")
				.slice(0, 120);
			if (label && !clicked.includes(label) && clicked.length < 20) clicked.push(label);
		};
		document.addEventListener("click", clickListener, true);

		const creationComplete = recordEvent({
			eventId,
			type: "VIEW",
			category: "navigation",
			module: route.module,
			entity: route.entity,
			entityId: route.entityId,
			actionLabel: `Opened ${route.labelPath.join(" > ")}`,
			targetUserId: route.entity === "Employee" ? route.entityId : null,
			navigation: {
				pathname: location.pathname,
				search: location.search.replace(/^\?/, ""),
				labelPath: route.labelPath,
				previousPathname: previousPathname || null,
				previousLabelPath: previous?.labelPath || [],
				openedAt: new Date(openedAt).toISOString()
			},
			metadata: { session: getClientSessionId() }
		})
			.then((result) => {
				if (result?.history)
					window.dispatchEvent(
						new CustomEvent("apit:activity-history", {
							detail: { history: result.history }
						})
					);
			})
			.catch((error) => console.warn("Navigation event failed", error));

		return () => {
			document.removeEventListener("click", clickListener, true);
			const height = Math.max(document.documentElement.scrollHeight - window.innerHeight, 0);
			const scrollDepth =
				height > 0 ? Math.min(100, Math.round((window.scrollY / height) * 100)) : 100;
			const returnedTo = window.location.pathname;
			window.sessionStorage.setItem("apit:previous-pathname", location.pathname);
			creationComplete
				.then(() =>
					finalizeEvent(eventId, {
						durationMs: Date.now() - openedAt,
						navigation: {
							closedAt: new Date().toISOString(),
							scrollDepth,
							clicked,
							returnedTo
						}
					})
				)
				.catch((error) => console.warn("Navigation finalization failed", error));
		};
	}, [location.pathname, location.search, user?._id]);

	// Socket message handler
	useEffect(() => {
		if (location.pathname.includes("chat")) return;

		const handleNewMessage = async (newMessage) => {
			if (location.pathname.includes(`chat/${newMessage.chatId}`)) return;
			if (location.pathname.includes(`chat?`)) return;
			if (newMessage.from._id !== user?._id) {
				try {
					const partner = await API.get({
						route: config.api.system.user,
						query: { _id: newMessage.from._id },
						uploadType: true,
						limit: 1,
						populate: ["department", "position", "role"]
					});

					const previewValue = newMessage?.previewText || newMessage?.body || "";
					const messageBody = previewValue
						? previewValue.length > 30
							? previewValue.slice(0, 30)
							: previewValue
						: "";

					const isMentionNotification = isUserMentionedInMessage({
						message: newMessage,
						userId: user?._id
					});
					setNewNotification({
						ID: new Date().getTime(),
						date: new Date().toISOString(),
						name: partner[0].name || {
							en: T("nothingFound", lang),
							ru: T("nothingFound", lang),
							hy: T("nothingFound", lang)
						},
						desc: {
							en: messageBody,
							ru: messageBody,
							hy: messageBody
						},
						importance: isMentionNotification ? "critical" : "medium",
						route: `${config.api.chat.chatGroup}/${newMessage.chatId}?messageId=${newMessage._id}`,
						createdByUser: partner[0],
						targetUsers: [
							{
								user: user?._id,
								read: false
							}
						]
					});
				} catch (error) {
					console.error("Error handling new message:", error);
				}
			}
		};

		socket?.on("newMessage", handleNewMessage);

		return () => {
			socket?.off("newMessage", handleNewMessage);
		};
		// eslint-disable-next-line
	}, [socket, location.pathname, user?._id, lang, setNewNotification]);

	// User activity tracking. The API owns presence; this only reports activity, throttled, and the API
	// also flips idle sessions to away on its own (so a missed event cannot leave a stale "online").
	useEffect(() => {
		const AWAY_AFTER_MS = 300000;
		const ACTIVITY_PING_MS = 30000;
		let lastActivity = Date.now();
		let lastReported = 0;
		let userStatus = "online";

		const reportOnline = () => {
			if (!socket?.connected) return;
			lastReported = Date.now();
			userStatus = "online";
			socket.emit("userOnline", user?._id);
		};
		const onActivity = () => {
			lastActivity = Date.now();
			if (userStatus !== "online" || lastActivity - lastReported >= ACTIVITY_PING_MS) reportOnline();
		};
		const onVisibility = () => {
			if (!document.hidden) onActivity();
		};

		const events = ["mousemove", "mousedown", "keydown", "wheel", "touchstart", "scroll", "focus"];
		events.forEach((name) => window.addEventListener(name, onActivity, { passive: true, capture: true }));
		document.addEventListener("visibilitychange", onVisibility);

		const checkConnection = setInterval(() => {
			if (Date.now() - lastActivity > AWAY_AFTER_MS && userStatus !== "away") {
				socket?.emit("userAway", user?._id);
				userStatus = "away";
			}
		}, 5000);

		return () => {
			clearInterval(checkConnection);
			events.forEach((name) => window.removeEventListener(name, onActivity, { capture: true }));
			document.removeEventListener("visibilitychange", onVisibility);
		};
	}, [socket, user?._id]);

	// Initial load
	useEffect(() => {
		setIsBusy(false);
	}, []);

	if (isBusy) {
		return <Loading />;
	}

	// Color the header to match the module (platform) we are currently in.
	const { platform: currentPlatform } = findCurrentPlatform(platforms, location.pathname);
	const activeModuleColor = currentPlatform?.color || null;
	const headerGlassBase =
		theme.colorScheme === "dark" ? "rgba(15, 18, 28, 0.60)" : "rgba(255, 255, 255, 0.55)";
	const headerBackground = activeModuleColor
		? `linear-gradient(0deg, ${hexToRgba(activeModuleColor, 0)}, ${hexToRgba(activeModuleColor, 0)}), ${headerGlassBase}`
		: headerGlassBase;
	const headerBorderBottom = activeModuleColor
		? `2px solid ${activeModuleColor}`
		: theme.colorScheme === "dark"
			? "1px solid rgba(255, 255, 255, 0.07)"
			: "1px solid rgba(0, 0, 0, 0.07)";

	return (
		<SecurityAlertsBadgeProvider>
			<AppShell
				padding={isMobile ? "xs" : "md"}
				navbarOffsetBreakpoint="md"
				styles={{
					navbar: {
						transitionProperty: "width, min-width, max-width, flex-basis",
						transitionDuration: `${SIDEBAR_TOGGLE_MS}ms`,
						transitionTimingFunction: SIDEBAR_TOGGLE_EASING,
						overflow: "hidden"
					},
					main: {
						transitionProperty: "padding-left, margin-left, max-width",
						transitionDuration: `${SIDEBAR_TOGGLE_MS}ms`,
						transitionTimingFunction: SIDEBAR_TOGGLE_EASING,
						...(isMobile ? { paddingLeft: 0 } : {})
					}
				}}
				header={
					<Header
						height={66}
						style={{
							display: "flex",
							alignItems: "center",
							width: "100%",
							padding: theme.spacing.sm,
							background: headerBackground,
							backdropFilter: "blur(28px)",
							WebkitBackdropFilter: "blur(28px)",
							borderBottom: headerBorderBottom,
							transition: "background 250ms ease, border-color 250ms ease",
							overflowX: "clip",
							overflowY: "visible",
							boxShadow:
								theme.colorScheme === "dark"
									? "0 2px 16px rgba(0,0,0,0.40), 0 1px 0 rgba(255,255,255,0.05)"
									: "0 2px 16px rgba(0,0,0,0.07), 0 1px 0 rgba(0,0,0,0.04)"
						}}
					>
						<ActivityTracker opened={platformMenuOpened} OpenClose={OpenClose} />

						<ActionIcon
							variant={opened ? "light" : "default"}
							size={isMobile ? "md" : "lg"}
							aria-label={opened ? T("closeNavigation", lang) : T("openNavigation", lang)}
							aria-expanded={opened}
							mr={isMobile ? 4 : 10}
							style={{ flexShrink: 0 }}
							onClick={toggle}
						>
							<IconLayoutSidebar size={isMobile ? 20 : 22} stroke={1.5} />
						</ActionIcon>
						<Link to="/" style={{ display: "flex", alignItems: "flex-center" }}>
							<Image src="/animated.svg" alt="APIT logo" height={isMobile ? 28 : 40} />
						</Link>
						{!isMobile && (
							<Flex direction="column">
								<Text size="xs" color="cyan" opacity={0.3} style={{ marginTop: 3, marginLeft: 10 }}>
									{config.lastUpdate}
								</Text>
								<Text size="sm" color="cyan" weight={700} style={{ marginTop: 5, marginLeft: 10 }}>
									v{config.version}
								</Text>
							</Flex>
						)}
						{!isMobile && (
							<Group spacing={6} noWrap style={{ flexShrink: 0, marginLeft: 8 }}>
								<Language isMobile={isMobile} compact />
								<ThemeToggle isMobile={isMobile} />
								<TrashBadge isMobile={isMobile} />
								<YerevanWeather isMobile={isMobile} lang={lang} />
							</Group>
						)}
						<Flex style={{ flex: 1, minWidth: 0, opacity: "0.8" }} justify="center" align="center">
							<GlobalSearch />
						</Flex>
						<Group
							spacing={isMobile ? 2 : 6}
							noWrap
							style={{
								flexShrink: 0,
								marginRight: isMobile ? 4 : 8,
								overflow: "visible"
							}}
						>
							<HeaderMenuButtons isMobile={isMobile} onAiAssistantOpen={assistantHandlers.open} />
							<PhotosPickerController />
							{isMobile && <YerevanWeather isMobile={isMobile} lang={lang} />}
							{isMobile && <ThemeToggle isMobile={isMobile} />}
							<Notifications isMobile={isMobile} compact />
							<div style={{ marginLeft: isMobile ? 4 : 10 }}>
								<UserInfoModal isMobile={isMobile} compact />
							</div>
						</Group>
					</Header>
				}
				navbar={
					isMobile ? undefined : (
						<Navbar
							p={0}
							width={{
								base: opened ? SIDEBAR_WIDTH_PX : 0,
								sm: opened ? SIDEBAR_WIDTH_PX : 0,
								lg: opened ? SIDEBAR_WIDTH_PX : 0
							}}
							hiddenBreakpoint={0}
							styles={{
								root: {
									flexShrink: 0,
									overflow: "hidden",
									pointerEvents: opened ? undefined : "none",
									background:
										theme.colorScheme === "dark"
											? "rgba(14, 17, 26, 0.55)"
											: "rgba(255, 255, 255, 0.50)",
									backdropFilter: "blur(28px)",
									WebkitBackdropFilter: "blur(28px)",
									transitionProperty:
										"width, min-width, max-width, flex-basis, box-shadow, background",
									transitionDuration: `${SIDEBAR_TOGGLE_MS}ms`,
									transitionTimingFunction: SIDEBAR_TOGGLE_EASING,
									border: "none",
									boxShadow: opened
										? theme.colorScheme === "dark"
											? "4px 0 24px rgba(0,0,0,0.40), 1px 0 0 rgba(255,255,255,0.06)"
											: "4px 0 24px rgba(0,0,0,0.08), 1px 0 0 rgba(0,0,0,0.05)"
										: "none"
								}
							}}
						>
							<Sidebar
								onNavigate={closeSidebarAfterNav}
								onAiAssistantOpen={assistantHandlers.open}
							/>
						</Navbar>
					)
				}
			>
				<BreadcrumbsComponent />
				<Component user={user} platforms={platforms} />
			</AppShell>
			<AssistantDrawer opened={assistantOpened} onClose={assistantHandlers.close} />
			{isMobile && (
				<Drawer
					opened={opened}
					onClose={closeSidebar}
					position="left"
					padding={0}
					size={300}
					withCloseButton={true}
					title={<Language isMobile compact />}
					transition="slide-right"
					transitionDuration={SIDEBAR_TOGGLE_MS}
					transitionTimingFunction={SIDEBAR_TOGGLE_EASING}
					overlayProps={{ opacity: 0.4, blur: 8, color: theme.black }}
					lockScroll
					trapFocus
					closeOnClickOutside
					closeOnEscape
					zIndex={250}
					styles={{
						drawer: {
							top: 0,
							height: "calc(100dvh - 66px)",
							display: "flex",
							flexDirection: "column",
							background:
								theme.colorScheme === "dark"
									? "rgba(14, 17, 26, 0.55)"
									: "rgba(255, 255, 255, 0.50)",
							backdropFilter: "blur(28px)",
							WebkitBackdropFilter: "blur(28px)"
						},
						header: {
							margin: 0,
							padding: "8px 12px",
							borderBottom: `1px solid ${
								theme.colorScheme === "dark" ? theme.colors.dark[5] : theme.colors.gray[2]
							}`
						},
						body: {
							flex: 1,
							minHeight: 0,
							padding: 0,
							display: "flex",
							flexDirection: "column",
							overflow: "hidden"
						},
						overlay: { top: 0 }
					}}
				>
					<Sidebar onNavigate={closeSidebarAfterNav} onAiAssistantOpen={assistantHandlers.open} />
				</Drawer>
			)}
		</SecurityAlertsBadgeProvider>
	);
}
